import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { bewebItem, catColor, dateClock, duration } from '../../components/parking/eventItems';
import {
    VISIT_CLS,
    VISIT_END,
    fetchVisit,
    imageUrl,
    visitEvents,
} from '../../helpers/parkingEvents';

export const DRAWER_WIDTH = 480;

const huf = (n) => `${new Intl.NumberFormat('hu-HU').format(n || 0)} Ft`;

function Shot({ label, point, onZoom }) {
    const url = imageUrl(point);
    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
                {label}
                {point ? ` · ${point.time.slice(11)}` : ''}
            </Typography>
            <Box
                sx={{
                    aspectRatio: '16 / 9',
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {url ? (
                    <Box
                        component="img"
                        src={url}
                        alt={label}
                        loading="lazy"
                        onClick={() => onZoom(url)}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            bgcolor: '#000',
                            cursor: 'zoom-in',
                        }}
                    />
                ) : (
                    <Typography variant="caption" color="text.secondary">
                        nincs kép
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

function Field({ label, children }) {
    return (
        <Stack direction="row" spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                {label}
            </Typography>
            <Typography variant="body2" component="div">
                {children}
            </Typography>
        </Stack>
    );
}

/**
 * One visit: entry / exit images, summary, the record's timeline from the entry to the end
 */
export default function VisitDrawer({ id, onClose }) {
    const [state, setState] = useState({ loading: false, visit: null, error: null });
    const [zoom, setZoom] = useState(null);

    useEffect(() => {
        if (!id) return undefined;
        let stop = false;
        setState({ loading: true, visit: null, error: null });
        fetchVisit(id)
            .then((visit) => !stop && setState({ loading: false, visit, error: null }))
            .catch((error) => !stop && setState({ loading: false, visit: null, error }));
        return () => {
            stop = true;
        };
    }, [id]);

    const v = state.visit;
    const end = v && (VISIT_END[v.end] || { label: v.end, color: 'default' });
    const events = v
        ? visitEvents(v)
              .map((e) => ({ ...e, item: bewebItem(e) }))
              .filter((e) => !e.item.noise)
        : [];

    return (
        <Drawer
            anchor="right"
            variant="persistent"
            open={!!id}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: DRAWER_WIDTH },
                    top: 64,
                    height: 'calc(100% - 64px)',
                },
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2, py: 1.5 }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                        variant="h5"
                        sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 }}
                    >
                        {v ? v.plate || '–' : ''}
                    </Typography>
                    {end && <Chip size="small" color={end.color} label={end.label} />}
                </Stack>
                <IconButton onClick={onClose} title="Bezárás">
                    <CloseIcon />
                </IconButton>
            </Stack>
            {state.loading && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                </Box>
            )}
            {state.error && (
                <Alert severity="error" sx={{ m: 2 }}>
                    {state.error.status === 404
                        ? 'Ez a látogatás nem található (újraépült a nap?)'
                        : state.error.message}
                </Alert>
            )}
            {v && (
                <>
                    <Stack direction="row" spacing={1} sx={{ px: 2 }}>
                        <Shot label="Behajtás" point={v.in} onZoom={setZoom} />
                        <Shot label="Kihajtás" point={v.out} onZoom={setZoom} />
                    </Stack>
                    <Stack spacing={0.5} sx={{ px: 2, py: 1.5 }}>
                        <Field label="Behajtás">
                            {v.in.time} · {v.in.gate}
                            {v.inReads ? ` (${v.inReads}× olvasva)` : ''}
                        </Field>
                        <Field label="Vége">
                            {v.out ? `${v.out.time} · ${v.out.gate}` : v.endTime || '–'}
                            {v.durMin != null ? ` · ${duration(v.durMin * 60e3)}` : ''}
                        </Field>
                        {v.release && (
                            <Field label="Kiengedés">
                                {v.release.type === 'opening'
                                    ? 'Kézi sorompónyitás'
                                    : '1x kihajtó jegy'}{' '}
                                · {v.release.time.slice(11)} · {v.release.gate}
                                {v.release.user ? ` · ${v.release.user}` : ''}
                            </Field>
                        )}
                        {v.releaseBefore && (
                            <Field label="Kiengedés?">
                                {v.releaseBefore.time.slice(11)} · {v.releaseBefore.gate} — utána
                                mégis kihajtott, nem ez az autó volt
                            </Field>
                        )}
                        <Field label="Fizetve">
                            {huf(v.paid)} ({v.payments} fizetés)
                        </Field>
                        {v.fee && (
                            <Field label="Díj lekérdezve">
                                {huf(v.fee.fee)} · {v.fee.time.slice(11)}
                                {v.fee.status ? ` · ${v.fee.status}` : ''}
                            </Field>
                        )}
                        <Field label="Elutasítás">{v.rejections} a kijáratoknál</Field>
                        <Field label="Típus">
                            {VISIT_CLS[v.cls] || v.cls} · {v.kind || '–'}
                        </Field>
                        {v.card && <Field label="Bérletkártya">{v.card}</Field>}
                        <Field label="be.web azonosító">{v.ident}</Field>
                    </Stack>
                    <Divider />
                    <List dense sx={{ overflowY: 'auto', flex: 1 }}>
                        {events.map((e) => {
                            const url = imageUrl(e);
                            return (
                                <ListItemButton
                                    key={e.k}
                                    disabled={!url}
                                    onClick={() => url && setZoom(url)}
                                    sx={{ '&.Mui-disabled': { opacity: 1 } }}
                                >
                                    <ListItemText
                                        primary={
                                            <Stack
                                                direction="row"
                                                spacing={1.5}
                                                alignItems="center"
                                            >
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        flexShrink: 0,
                                                        borderRadius:
                                                            e.item.sev === 'fail' ? 0 : '50%',
                                                        transform:
                                                            e.item.sev === 'fail'
                                                                ? 'rotate(45deg)'
                                                                : 'none',
                                                        bgcolor: catColor(e.item.cat),
                                                    }}
                                                />
                                                <span>{dateClock(e.ts)}</span>
                                                <Typography
                                                    variant="body2"
                                                    component="span"
                                                    color={
                                                        e.item.sev === 'fail'
                                                            ? 'error.main'
                                                            : 'text.primary'
                                                    }
                                                    sx={{
                                                        fontWeight:
                                                            e.item.sev === 'info' ? 400 : 700,
                                                    }}
                                                >
                                                    {e.item.label}
                                                </Typography>
                                            </Stack>
                                        }
                                        secondary={[
                                            e.item.gateLabel,
                                            e.amount ? huf(e.amount) : '',
                                            e.user,
                                            e.plate && e.plate !== v.plate ? e.plate : '',
                                            url ? 'kép' : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                </>
            )}
            <Dialog open={!!zoom} onClose={() => setZoom(null)} maxWidth="xl">
                {zoom && (
                    <Box
                        component="img"
                        src={zoom}
                        alt=""
                        onClick={() => setZoom(null)}
                        sx={{ maxWidth: '96vw', maxHeight: '92vh', display: 'block' }}
                    />
                )}
            </Dialog>
        </Drawer>
    );
}
